import React, { useState, useEffect, useMemo, useRef } from 'react';
import { useLocation, useNavigate, Link } from 'react-router-dom';
import { MapPin, Search, Clock, Heart, Filter, ChevronDown, Store, ImageOff, X } from 'lucide-react';
import { useFavoritesContext } from '../../hooks/useFavorites';
import { useBoutiques, BoutiqueWithDistance } from '../../hooks/useBoutiques';
import { collection, query, where, orderBy, limit, getDocs } from 'firebase/firestore';
import { db } from '../../config/firebase';
import { Product } from '../../hooks/useProducts';
import { BackButton } from '../../components/ui/BackButton';
import { ShopperBottomNav } from '../../components/shopper/ShopperBottomNav';
import { ShopperNavActions } from '../../components/shopper/ShopperNavActions';
import { SearchOverlay } from '../../components/shopper/SearchOverlay';
import { LocationPickerSheet } from '../../components/shopper/LocationPickerSheet';
import { useShopperLocation } from '../../hooks/useShopperLocation';
import { useAuth } from '../../hooks/useAuth';
import { formatMiles } from '../../lib/geo';
import { SHOPPER_HOME } from '../../constants/routes';

const SORT_OPTIONS = [
  { label: 'Nearest', value: 'distance_asc' },
  { label: 'Newest', value: 'newest' },
  { label: 'Price: Low to High', value: 'price_asc' },
  { label: 'Price: High to Low', value: 'price_desc' },
  { label: 'Store Name A-Z', value: 'name_asc' },
  { label: 'Highest Rated', value: 'rating_desc' },
];

const PRICE_RANGES = [
  { label: 'All Prices', min: undefined as number | undefined, max: undefined as number | undefined },
  { label: 'Under $50', min: 0, max: 50 },
  { label: '$50 - $100', min: 50, max: 100 },
  { label: '$100 - $200', min: 100, max: 200 },
  { label: '$200 - $500', min: 200, max: 500 },
  { label: '$500+', min: 500, max: undefined },
];

const PRODUCT_CATEGORIES = [
  'Dresses', 'Tops', 'Bottoms', 'Outerwear', 'Swimwear',
  'Activewear', 'Accessories', 'Shoes', 'Jewelry', 'Bags',
  'Jumpsuits', 'Lingerie', 'Sets', 'Other',
];

// Catalogue-wide cap so a large product collection can't turn this page into a
// full-collection read. Enough to fill the 6-per-boutique carousels.
const PRODUCT_FETCH_LIMIT = 300;

// Untagged items in these categories are treated as women's only; everything
// else is shown in both tabs so the Men tab isn't empty before merchants start
// tagging gender.
const WOMEN_ONLY_CATEGORIES = new Set(['Dresses', 'Lingerie', 'Jumpsuits', 'Sets', 'Swimwear']);

const MEN_TAGS = ["men", "men's", 'mens', 'menswear', 'male'];
const WOMEN_TAGS = ["women", "women's", 'womens', 'womenswear', 'female'];

function FallbackImg({ src, alt, className, fallbackIcon }: { src: string; alt: string; className: string; fallbackIcon?: 'store' | 'image' }) {
  const [failed, setFailed] = React.useState(false);
  if (!src || failed) {
    return (
      <div className={`${className} flex items-center justify-center bg-gray-200`}>
        {fallbackIcon === 'image' ? (
          <ImageOff className="h-6 w-6 text-gray-400" />
        ) : (
          <Store className="h-6 w-6 text-gray-400" />
        )}
      </div>
    );
  }
  return <img src={src} alt={alt} className={className} onError={() => setFailed(true)} />;
}

function matchesGenderTab(product: Product, tab: 'women' | 'men'): boolean {
  const tags = (product.tags || []).map((t) => t.trim().toLowerCase());
  const taggedMen = tags.some((t) => MEN_TAGS.includes(t));
  const taggedWomen = tags.some((t) => WOMEN_TAGS.includes(t));

  if (taggedMen && !taggedWomen) return tab === 'men';
  if (taggedWomen && !taggedMen) return tab === 'women';
  if (taggedMen && taggedWomen) return true;

  // Untagged: women-specific categories stay on the Women tab, the rest are
  // treated as unisex and appear on both.
  if (WOMEN_ONLY_CATEGORIES.has(product.category)) return tab === 'women';
  return true;
}

function productTimestamp(p: Product): number {
  const raw = p.createdAt as unknown;
  if (raw && typeof raw === 'object' && 'toMillis' in raw && typeof (raw as { toMillis: () => number }).toMillis === 'function') {
    return (raw as { toMillis: () => number }).toMillis();
  }
  if (raw instanceof Date) return raw.getTime();
  return 0;
}

function sortProducts(products: Product[], sortBy: string): Product[] {
  const sorted = [...products];
  switch (sortBy) {
    case 'price_asc':
      return sorted.sort((a, b) => a.price - b.price);
    case 'price_desc':
      return sorted.sort((a, b) => b.price - a.price);
    case 'newest':
    default:
      return sorted.sort((a, b) => productTimestamp(b) - productTimestamp(a));
  }
}

function sortBoutiques(
  stores: BoutiqueWithDistance[],
  sortBy: string
): BoutiqueWithDistance[] {
  const sorted = [...stores];
  switch (sortBy) {
    case 'distance_asc':
      return sorted.sort((a, b) => {
        if (a.distanceMiles === null) return 1;
        if (b.distanceMiles === null) return -1;
        return a.distanceMiles - b.distanceMiles;
      });
    case 'name_asc':
      return sorted.sort((a, b) => a.name.localeCompare(b.name));
    case 'rating_desc':
      return sorted.sort((a, b) => (b.rating || 0) - (a.rating || 0));
    default:
      return sorted;
  }
}

function userInitials(user: { displayName?: string | null; email?: string | null }): string {
  if (user.displayName) {
    const parts = user.displayName.trim().split(/\s+/);
    if (parts.length >= 2) return (parts[0][0] + parts[1][0]).toUpperCase();
    return parts[0].slice(0, 2).toUpperCase();
  }
  if (user.email) return user.email.slice(0, 2).toUpperCase();
  return '?';
}

export function BoutiquesListPage() {
  const navigate = useNavigate();
  const routeLocation = useLocation();
  const favorites = useFavoritesContext();
  const { user } = useAuth();
  const { location: shopperLocation, label: locationLabel, hasCoords, resetLocation } = useShopperLocation();

  const [activeTab, setActiveTab] = useState<'women' | 'men'>('women');
  const [sortBy, setSortBy] = useState('newest');
  const [showSortMenu, setShowSortMenu] = useState(false);
  const [showFilterPanel, setShowFilterPanel] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState<string | null>(null);
  const [selectedPriceRange, setSelectedPriceRange] = useState(0);
  const [showSearch, setShowSearch] = useState(false);
  const [showAddressPicker, setShowAddressPicker] = useState(false);
  const [allProducts, setAllProducts] = useState<Product[]>([]);
  const [productsLoading, setProductsLoading] = useState(true);

  const sortMenuRef = useRef<HTMLDivElement>(null);

  const nearOrigin =
    hasCoords && shopperLocation?.lat != null && shopperLocation?.lng != null
      ? { lat: shopperLocation.lat, lng: shopperLocation.lng }
      : null;

  const { boutiques, loading: boutiquesLoading } = useBoutiques({
    near: nearOrigin,
    filterByDelivery: true,
  });

  useEffect(() => {
    let cancelled = false;
    setProductsLoading(true);
    const q = query(
      collection(db, 'products'),
      where('active', '==', true),
      orderBy('createdAt', 'desc'),
      limit(PRODUCT_FETCH_LIMIT)
    );
    getDocs(q)
      .then((snap) => {
        if (cancelled) return;
        setAllProducts(snap.docs.map((d) => ({ id: d.id, ...d.data() } as Product)));
      })
      .catch(() => {
        if (!cancelled) setAllProducts([]);
      })
      .finally(() => {
        if (!cancelled) setProductsLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    if (!showSortMenu) return;
    const handleClick = (e: MouseEvent) => {
      if (sortMenuRef.current && !sortMenuRef.current.contains(e.target as Node)) {
        setShowSortMenu(false);
      }
    };
    const handleKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setShowSortMenu(false);
    };
    document.addEventListener('mousedown', handleClick);
    document.addEventListener('keydown', handleKey);
    return () => {
      document.removeEventListener('mousedown', handleClick);
      document.removeEventListener('keydown', handleKey);
    };
  }, [showSortMenu]);

  const priceRange = PRICE_RANGES[selectedPriceRange];
  const hasActiveFilters = selectedCategory !== null || selectedPriceRange > 0;

  const { filteredBoutiques, storeProducts, totalResults } = useMemo(() => {
    const filteredProducts = allProducts.filter((p) => {
      if (!matchesGenderTab(p, activeTab)) return false;
      if (selectedCategory && p.category !== selectedCategory) return false;
      if (priceRange.min !== undefined && p.price < priceRange.min) return false;
      if (priceRange.max !== undefined && p.price > priceRange.max) return false;
      return true;
    });

    const productSortKey =
      sortBy === 'price_asc' || sortBy === 'price_desc' || sortBy === 'newest'
        ? sortBy
        : 'newest';

    const byBrand: Record<string, Product[]> = {};
    for (const p of filteredProducts) {
      if (!p.brandId) continue;
      if (!byBrand[p.brandId]) byBrand[p.brandId] = [];
      byBrand[p.brandId].push(p);
    }

    const productMap: Record<string, Product[]> = {};
    for (const [brandId, products] of Object.entries(byBrand)) {
      productMap[brandId] = sortProducts(products, productSortKey).slice(0, 6);
    }

    const visibleBoutiques = boutiques.filter((b) => (productMap[b.id]?.length ?? 0) > 0);
    const sortedBoutiques = sortBoutiques(visibleBoutiques, sortBy);

    // Count only what is actually rendered — products whose boutique was
    // filtered out (delivery radius, inactive) must not inflate the total.
    const total = visibleBoutiques.reduce(
      (sum, b) => sum + (productMap[b.id]?.length ?? 0),
      0
    );

    return {
      filteredBoutiques: sortedBoutiques,
      storeProducts: productMap,
      totalResults: total,
    };
  }, [
    boutiques,
    allProducts,
    activeTab,
    selectedCategory,
    priceRange,
    sortBy,
  ]);

  const sortOptions = useMemo(() => {
    if (hasCoords) return SORT_OPTIONS;
    return SORT_OPTIONS.filter((o) => o.value !== 'distance_asc');
  }, [hasCoords]);

  // Losing coordinates removes "Nearest", so drop back to a sort that still exists.
  useEffect(() => {
    if (!sortOptions.some((o) => o.value === sortBy)) {
      setSortBy('newest');
    }
  }, [sortOptions, sortBy]);

  const activeSortLabel = sortOptions.find((o) => o.value === sortBy)?.label || 'Newest';

  const clearFilters = () => {
    setSelectedCategory(null);
    setSelectedPriceRange(0);
  };

  const loading = boutiquesLoading || productsLoading;

  // Distinguish "nothing delivers here" from "your filters excluded everything"
  // so the empty state offers the action that actually helps.
  const noBoutiquesInRange = boutiques.length === 0 && !!nearOrigin;
  const emptyStateMessage = noBoutiquesInRange
    ? 'No boutiques deliver to this address yet.'
    : hasActiveFilters
      ? 'No results match your filters.'
      : boutiques.length === 0
        ? 'No boutiques available yet. Check back soon!'
        : activeTab === 'men'
          ? "No men's items from these boutiques yet."
          : 'No products available from these boutiques yet.';

  return (
    <div className="min-h-screen bg-white pb-20 md:pb-0">
      <SearchOverlay isOpen={showSearch} onClose={() => setShowSearch(false)} />
      <LocationPickerSheet
        isOpen={showAddressPicker}
        onClose={() => setShowAddressPicker(false)}
      />

      {/* Header */}
      <header className="sticky top-0 z-40 bg-white border-b border-gray-100">
        <div className="px-4 py-3">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3 min-w-0">
              <BackButton
                fallback={SHOPPER_HOME}
                className="p-1 hover:bg-gray-100 rounded-full transition-colors flex-shrink-0"
                iconClassName="h-5 w-5 text-heading"
              />
              <button
                type="button"
                onClick={() => setShowAddressPicker(true)}
                className="flex items-center gap-1 text-sm min-w-0"
              >
                <MapPin className="h-4 w-4 text-gray-400 flex-shrink-0" />
                <span className="underline text-heading truncate">
                  {locationLabel}
                </span>
              </button>
            </div>
            <div className="flex items-center gap-2 flex-shrink-0">
              <button
                type="button"
                onClick={() => setShowSearch(true)}
                className="p-2 hover:bg-gray-100 rounded-full transition-colors"
                aria-label="Search"
              >
                <Search className="h-5 w-5 text-heading" />
              </button>
              {user ? (
                <Link
                  to="/settings"
                  className="w-8 h-8 rounded-full bg-pink-100 flex items-center justify-center overflow-hidden hover:ring-2 hover:ring-primary/30 transition-all"
                  aria-label="Account settings"
                >
                  {user.photoURL ? (
                    <img src={user.photoURL} alt="" className="w-full h-full object-cover" />
                  ) : (
                    <span className="text-xs font-semibold text-primary">{userInitials(user)}</span>
                  )}
                </Link>
              ) : (
                <Link
                  to="/signin"
                  className="w-8 h-8 rounded-full bg-pink-100 flex items-center justify-center hover:ring-2 hover:ring-primary/30 transition-all"
                  aria-label="Sign in"
                >
                  <span className="text-xs font-semibold text-primary">?</span>
                </Link>
              )}
              <ShopperNavActions
                className="p-2 hover:bg-gray-100 rounded-full transition-colors"
                iconClassName="h-5 w-5 text-heading"
              />
            </div>
          </div>
        </div>
      </header>

      {/* Women/Men Toggle */}
      <div className="px-4 py-4">
        <div className="flex rounded-full border border-primary overflow-hidden">
          <button
            type="button"
            onClick={() => setActiveTab('women')}
            className={`flex-1 py-2.5 text-sm font-medium transition-colors ${
              activeTab === 'women' ? 'bg-primary text-white' : 'bg-white text-primary'
            }`}
          >
            Women
          </button>
          <button
            type="button"
            onClick={() => setActiveTab('men')}
            className={`flex-1 py-2.5 text-sm font-medium transition-colors ${
              activeTab === 'men' ? 'bg-primary text-white' : 'bg-white text-primary'
            }`}
          >
            Men
          </button>
        </div>
      </div>

      {/* Results & Filters */}
      <div className="px-4 py-2 flex items-center justify-between relative">
        <span className="text-sm text-body">{totalResults} Results</span>
        <div className="flex items-center gap-4">
          <div className="relative" ref={sortMenuRef}>
            <button
              type="button"
              onClick={() => {
                setShowSortMenu((v) => !v);
                setShowFilterPanel(false);
              }}
              className="flex items-center gap-1 text-sm text-body"
            >
              {activeSortLabel} <ChevronDown className="h-4 w-4" />
            </button>
            {showSortMenu && (
              <div className="absolute right-0 top-full mt-1 w-48 bg-white border border-gray-200 rounded-xl shadow-lg z-30 py-1">
                {sortOptions.map((opt) => (
                  <button
                    key={opt.value}
                    type="button"
                    onClick={() => {
                      setSortBy(opt.value);
                      setShowSortMenu(false);
                    }}
                    className={`w-full text-left px-4 py-2.5 text-sm transition-colors ${
                      sortBy === opt.value
                        ? 'bg-primary/10 text-primary font-medium'
                        : 'text-heading hover:bg-gray-50'
                    }`}
                  >
                    {opt.label}
                  </button>
                ))}
              </div>
            )}
          </div>
          <button
            type="button"
            onClick={() => {
              setShowFilterPanel((v) => !v);
              setShowSortMenu(false);
            }}
            className={`flex items-center gap-1 text-sm ${
              showFilterPanel || hasActiveFilters ? 'text-primary font-medium' : 'text-body'
            }`}
          >
            Filter
            {hasActiveFilters && (
              <span className="bg-primary text-white text-[10px] rounded-full h-4 w-4 flex items-center justify-center">
                {(selectedCategory ? 1 : 0) + (selectedPriceRange > 0 ? 1 : 0)}
              </span>
            )}
            <Filter className="h-4 w-4" />
          </button>
        </div>
      </div>

      {/* Filter Panel */}
      {showFilterPanel && (
        <div className="mx-4 mb-3 p-4 bg-gray-50 rounded-xl border border-gray-200">
          <div className="flex items-center justify-between mb-3">
            <h3 className="text-sm font-medium text-heading">Filters</h3>
            {hasActiveFilters && (
              <button
                type="button"
                onClick={clearFilters}
                className="text-xs text-primary hover:text-primary/80"
              >
                Clear all
              </button>
            )}
          </div>
          <div className="space-y-4">
            <div>
              <label className="text-xs font-medium text-body uppercase tracking-wider mb-1.5 block">
                Category
              </label>
              <div className="flex flex-wrap gap-1.5">
                <button
                  type="button"
                  onClick={() => setSelectedCategory(null)}
                  className={`px-3 py-1 rounded-full text-xs font-medium transition-colors ${
                    !selectedCategory
                      ? 'bg-gray-900 text-white'
                      : 'bg-white border border-gray-200 text-heading hover:bg-gray-100'
                  }`}
                >
                  All
                </button>
                {PRODUCT_CATEGORIES.map((cat) => (
                  <button
                    key={cat}
                    type="button"
                    onClick={() => setSelectedCategory(cat)}
                    className={`px-3 py-1 rounded-full text-xs font-medium transition-colors ${
                      selectedCategory === cat
                        ? 'bg-gray-900 text-white'
                        : 'bg-white border border-gray-200 text-heading hover:bg-gray-100'
                    }`}
                  >
                    {cat}
                  </button>
                ))}
              </div>
            </div>
            <div>
              <label className="text-xs font-medium text-body uppercase tracking-wider mb-1.5 block">
                Price
              </label>
              <div className="flex flex-wrap gap-1.5">
                {PRICE_RANGES.map((range, i) => (
                  <button
                    key={range.label}
                    type="button"
                    onClick={() => setSelectedPriceRange(i)}
                    className={`px-3 py-1 rounded-full text-xs font-medium transition-colors ${
                      i === selectedPriceRange
                        ? 'bg-gray-900 text-white'
                        : 'bg-white border border-gray-200 text-heading hover:bg-gray-100'
                    }`}
                  >
                    {range.label}
                  </button>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Active filter pills */}
      {hasActiveFilters && !showFilterPanel && (
        <div className="px-4 pb-2 flex flex-wrap gap-2">
          {selectedCategory && (
            <span className="flex items-center gap-1 px-3 py-1 bg-gray-100 rounded-full text-xs font-medium text-heading">
              {selectedCategory}
              <button type="button" onClick={() => setSelectedCategory(null)}>
                <X className="h-3 w-3" />
              </button>
            </span>
          )}
          {selectedPriceRange > 0 && (
            <span className="flex items-center gap-1 px-3 py-1 bg-gray-100 rounded-full text-xs font-medium text-heading">
              {PRICE_RANGES[selectedPriceRange].label}
              <button type="button" onClick={() => setSelectedPriceRange(0)}>
                <X className="h-3 w-3" />
              </button>
            </span>
          )}
        </div>
      )}

      {/* Store Cards */}
      <div className="divide-y divide-gray-100">
        {loading ? (
          <div className="py-8">
            {[1, 2, 3].map((i) => (
              <div key={i} className="bg-gray-50 py-4 animate-pulse">
                <div className="px-4 flex items-center gap-3 mb-3">
                  <div className="w-12 h-12 rounded-full bg-gray-200" />
                  <div>
                    <div className="h-4 w-24 bg-gray-200 rounded" />
                    <div className="h-3 w-32 bg-gray-200 rounded mt-1" />
                  </div>
                </div>
                <div className="flex gap-3 px-4">
                  {[1, 2, 3].map((j) => (
                    <div key={j} className="flex-shrink-0 w-36 aspect-[3/4] bg-gray-200 rounded-lg" />
                  ))}
                </div>
              </div>
            ))}
          </div>
        ) : filteredBoutiques.length === 0 ? (
          <div className="text-center py-16 px-4">
            <Store className="h-12 w-12 mx-auto mb-3 text-gray-300" />
            <p className="text-body">{emptyStateMessage}</p>
            {hasActiveFilters && (
              <button
                type="button"
                onClick={clearFilters}
                className="mt-4 text-sm text-primary font-medium hover:underline"
              >
                Clear filters
              </button>
            )}
            {!hasActiveFilters && noBoutiquesInRange && (
              <button
                type="button"
                onClick={resetLocation}
                className="mt-4 text-sm text-primary font-medium hover:underline"
              >
                Change delivery address
              </button>
            )}
            {!hasActiveFilters && !noBoutiquesInRange && activeTab === 'men' && (
              <button
                type="button"
                onClick={() => setActiveTab('women')}
                className="mt-4 text-sm text-primary font-medium hover:underline"
              >
                Browse women&apos;s instead
              </button>
            )}
          </div>
        ) : (
          filteredBoutiques.map((store) => {
            const products = storeProducts[store.id] || [];
            const dist = store.distanceMiles;
            return (
              <div key={store.id} className="bg-gray-50 py-4">
                <div
                  className="px-4 flex items-center justify-between mb-3 cursor-pointer"
                  onClick={() =>
                    navigate(`/boutique/${store.id}`, { state: { from: routeLocation.pathname } })
                  }
                >
                  <div className="flex items-center gap-3">
                    <div className="w-12 h-12 rounded-full bg-gray-200 overflow-hidden">
                      <FallbackImg
                        src={store.logo || store.coverImage}
                        alt={store.name}
                        className="w-full h-full object-cover"
                      />
                    </div>
                    <div>
                      <h3 className="font-medium text-heading">{store.name}</h3>
                      <p className="text-xs text-body">
                        {store.formattedAddress ||
                          (typeof store.address === 'string'
                            ? store.address
                            : store.address
                              ? `${store.address.city}, ${store.address.state}`
                              : '')}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center gap-1 text-sm text-body">
                    <Clock className="h-4 w-4" />
                    <span>{dist != null ? formatMiles(dist) : 'Same-day'}</span>
                  </div>
                </div>

                {products.length > 0 ? (
                  <div className="flex gap-3 overflow-x-auto px-4 pb-2 scrollbar-hide">
                    {products.map((product) => (
                      <div
                        key={product.id}
                        className="flex-shrink-0 w-36"
                        onClick={() =>
                          navigate(`/product/${product.id}`, {
                            state: { from: routeLocation.pathname },
                          })
                        }
                      >
                        <div className="relative aspect-[3/4] rounded-lg overflow-hidden bg-white">
                          <img
                            src={product.images?.[0] || ''}
                            alt={product.name}
                            className="w-full h-full object-cover"
                          />
                          <button
                            type="button"
                            className="absolute top-2 right-2 p-1.5 bg-white/80 rounded-full"
                            onClick={(e) => {
                              e.stopPropagation();
                              favorites.toggleFavorite({
                                productId: product.id,
                                name: product.name,
                                brand: product.brand,
                                price: product.price,
                                imageUrl: product.images?.[0] || '',
                              });
                            }}
                          >
                            <Heart
                              className={`h-4 w-4 transition-colors ${
                                favorites.isFavorited(product.id)
                                  ? 'fill-primary text-primary'
                                  : 'text-primary'
                              }`}
                            />
                          </button>
                        </div>
                        <p className="mt-2 text-sm font-medium text-heading">${product.price}</p>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="px-4 py-4 text-center text-xs text-body">
                    No products listed yet
                  </div>
                )}
              </div>
            );
          })
        )}
      </div>

      <ShopperBottomNav />
    </div>
  );
}
